import './BlogDetails.css';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../../components/Header/Header';
import ChatWidget from '../../components/ChatWidget/ChatWidget';
import SubTitle from './SubTitleBlog';
import { Fragment } from 'react/jsx-runtime';
import BlogCard from './BlogCard';
import { useBlogTitles, useBlogContent } from './BlogContent';
import { useTranslation } from 'react-i18next';
import { FaInstagram, FaLinkedin } from "react-icons/fa";
import Seo from '../../seo/Seo';
import { ORGANIZATION_REF, absoluteUrl } from '../../seo/site';
import NotFound from '../NotFound/NotFound';

// Cast icons to any to resolve TypeScript S2786 error with React 19 types
const InstagramIcon = FaInstagram as any;
const LinkedinIcon = FaLinkedin as any;

export default function BlogDetails() {
  const { t } = useTranslation();
  const blogs = useBlogContent();
  const posts = useBlogTitles();
  const { id } = useParams<{ id: string }>();
  const blogDetails = blogs.find(b => b.id === Number(id));
  const post = posts.find(p => p.id === Number(id));
  const related = posts.filter(p => p.id !== Number(id)).slice(0, 3);

  if (!blogDetails || !post) return <NotFound />;

  const path = `/blog/${post.id}`;
  const image = blogDetails.image || post.imagem;

  function renderContentWithIndent(content?: string) {
    if (!content) return null;

    const parseBold = (text: string) => {
      const parts = text.split(/(\*\*.*?\*\*)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
    };

    return content.split('\n').map((line, idx) => (
      <Fragment key={idx}>
        {line.trim().startsWith('•') ? (
          <span className="cz-post__bullet">{parseBold(line)}</span>
        ) : (
          parseBold(line)
        )}
        <br />
      </Fragment>
    ));
  }

  return (
    <div className="cz-page cz-scope">
      <Seo
        title={blogDetails.title}
        description={post.excerpt}
        path={path}
        image={image}
        type="article"
        jsonLd={{
          "@type": "BlogPosting",
          headline: blogDetails.title,
          description: post.excerpt,
          datePublished: post.rawDate,
          image: image ? absoluteUrl(image) : undefined,
          mainEntityOfPage: absoluteUrl(path),
          author: ORGANIZATION_REF,
          publisher: ORGANIZATION_REF,
        }}
      />
      <Header />
      <main>
        <article className="cz-post">
          <header className="cz-post__head">
            <Link to="/blog" className="cz-post__back">
              <ArrowLeft size={16} aria-hidden="true" />
              {t('blog.back_to_blog', 'Blog')}
            </Link>
            <h1 className="cz-post__title">{blogDetails?.title}</h1>
            {post && <span className="cz-post__date">{post.data}</span>}
          </header>

          {blogDetails?.image && (
            <div className="cz-post__cover">
              <img src={blogDetails.image} alt={blogDetails.title} />
            </div>
          )}

          <div className="cz-post__body">
            {blogDetails?.content.map((section, index) => (
              <section key={index} className="cz-post__section">
                {section.subtitle && <SubTitle>{section.subtitle}</SubTitle>}
                {section.image && <img src={section.image} alt={section.subtitle || blogDetails.title} loading="lazy" className="cz-post__inline-img" />}
                {section.content && <p className="cz-post__text">{renderContentWithIndent(section.content)}</p>}
              </section>
            ))}
            <p className="cz-post__cta">{t('blog.cta')}</p>

            {blogDetails?.id === 10 && (
              <div className="cz-post__social">
                <a href="https://www.instagram.com/calrizig/" target="_blank" rel="noopener noreferrer" className="cz-post__social-link">
                  <span className="cz-post__social-icon"><InstagramIcon /></span>
                  @calrizig
                </a>
                <a href="https://www.instagram.com/digiprofe/" target="_blank" rel="noopener noreferrer" className="cz-post__social-link">
                  <span className="cz-post__social-icon"><InstagramIcon /></span>
                  @digiprofe
                </a>
                <a href="https://www.linkedin.com/company/calriz/" target="_blank" rel="noopener noreferrer" className="cz-post__social-link">
                  <span className="cz-post__social-icon"><LinkedinIcon /></span>
                  Calriz Linkedin
                </a>
              </div>
            )}
          </div>
        </article>

        <section className="cz-post-related">
          <h2 className="cz-post-related__title">{t('blog.read_more_title', 'Leia mais')}</h2>
          <div className="cz-post-related__grid">
            {related.map(item => (
              <BlogCard blog={item} key={item.id} />
            ))}
          </div>
        </section>
      </main>
      <ChatWidget />
    </div>
  );
}
